
import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle, User2, Youtube } from 'lucide-react';
import { YouTubeChannel, YouTubeChannelSelectionProps } from '../types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';

const YouTubeChannelSelection = ({
  channels,
  email,
  onSelectChannel,
  onCancel,
  remainingProfiles = Infinity,
  isLoading = false
}: YouTubeChannelSelectionProps) => {
  if (isLoading) {
    return (
      <Card className="bg-white rounded-lg shadow-sm overflow-hidden max-w-3xl mx-auto">
        <CardHeader className="p-4 border-b border-gray-100 flex items-center">
          <Button variant="ghost" size="icon" onClick={onCancel} className="mr-2">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-medium">Sélectionner une chaîne YouTube</h2>
        </CardHeader>
        
        <CardContent className="p-6">
          <div className="flex flex-col items-center justify-center py-12">
            <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600">Chargement de vos chaînes YouTube...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white rounded-lg shadow-sm overflow-hidden max-w-3xl mx-auto">
      <CardHeader className="p-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center">
          <Button variant="ghost" size="icon" onClick={onCancel} className="mr-2">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-medium">Sélectionner une chaîne YouTube</h2>
        </div>
        {remainingProfiles !== Infinity && (
          <Badge variant="outline" className="ml-2">
            {remainingProfiles} profils restants dans votre plan
          </Badge>
        )}
      </CardHeader>
      
      <CardContent className="p-6">
        <div className="mb-6">
          <div className="flex items-center mb-3">
            <Youtube className="h-5 w-5 text-red-600 mr-2" />
            <p className="font-medium text-gray-800">
              Compte connecté
            </p>
          </div>
          <p className="text-sm text-gray-600 ml-7">
            Veuillez sélectionner la chaîne YouTube que vous souhaitez connecter à votre profil
          </p>
        </div>
        
        <Separator className="my-4" />
        
        {channels.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="bg-gray-100 p-6 rounded-full mb-6">
              <User2 className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-medium mb-2">Aucune chaîne trouvée</h3>
            <p className="text-gray-600 mb-6 max-w-md">
              Nous n'avons trouvé aucune chaîne YouTube associée à votre compte {email}.
            </p>
            <Button onClick={onCancel} variant="outline">
              Retour
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 gap-4">
              {channels.map((channel) => (
                <div 
                  key={channel.id}
                  className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => onSelectChannel(channel)}
                >
                  <Avatar className="h-16 w-16 mr-4">
                    <AvatarImage src={channel.thumbnailUrl} alt={channel.title} />
                    <AvatarFallback className="bg-gray-200">
                      <User2 className="h-6 w-6 text-gray-400" />
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-grow">
                    <h3 className="font-medium text-gray-900">{channel.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                      {channel.description || "Aucune description"}
                    </p>
                    <div className="flex items-center mt-2 space-x-3 text-xs text-gray-500">
                      <span className="font-medium">{channel.subscriberCount?.toLocaleString() || 0} abonnés</span>
                      <span>•</span>
                      <span>{channel.videoCount?.toLocaleString() || 0} vidéos</span>
                    </div>
                  </div>
                  
                  <Button 
                    size="sm"
                    className="ml-4 whitespace-nowrap gap-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Connecter
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="mt-6 flex justify-end">
          <Button 
            variant="outline" 
            onClick={onCancel}
          >
            Annuler
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default YouTubeChannelSelection;
